import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getLogoUrl, getProfilePhotoUrl, getIdentityDocUrl, getPaymentQrImageUrl } from '../config';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import { Shield, Home, Users, FolderOpen, Briefcase, AlertTriangle, Zap, ClipboardList, Plus, Package, DollarSign, CreditCard, Megaphone, Crown, MessageCircle, BarChart3, FileText, Settings, Search, Trash2, Clock, Check, CheckCircle, X, XCircle, Calendar, User, Eye, Film, Pencil, Lightbulb, Save, Smartphone, Camera, Loader2, Upload } from 'lucide-react';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [authorRequests, setAuthorRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchUsers, setSearchUsers] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [stats, setStats] = useState({
    users: 0, authors: 0, admins: 0, projects: 0, services: 0, pendingRequests: 0,
    overduePayments: 0, pendingReports: 0, pendingDeletionRequests: 0
  });
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    titre: '', description: '', categorie: 'autre', tarif: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  // État pour le modal de décision
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decisionType, setDecisionType] = useState('');
  const [justification, setJustification] = useState('');
  // États pour filtrage/recherche/tri des demandes
  const [searchRequests, setSearchRequests] = useState('');
  const [filterRequestStatus, setFilterRequestStatus] = useState('all');
  const [sortRequests, setSortRequests] = useState('newest');
  
  // Guide de paiement
  const [paymentGuide, setPaymentGuide] = useState('');
  const [paymentGuideLoading, setPaymentGuideLoading] = useState(false);
  const [paymentGuideSaved, setPaymentGuideSaved] = useState(false);
  
  // Image QR de paiement
  const [paymentQrImage, setPaymentQrImage] = useState('');
  const [paymentQrFile, setPaymentQrFile] = useState(null);
  const [paymentQrPreview, setPaymentQrPreview] = useState(null);
  const [paymentQrLoading, setPaymentQrLoading] = useState(false);
  
  // Pagination
  const [usersPage, setUsersPage] = useState(1);
  const [projectsPage, setProjectsPage] = useState(1);
  const [servicesPage, setServicesPage] = useState(1);
  const [requestsPage, setRequestsPage] = useState(1);
  const [searchProjects, setSearchProjects] = useState('');
  const [searchServices, setSearchServices] = useState('');
  const itemsPerPage = 10;
  
  // Permissions admin
  const [adminPermissions, setAdminPermissions] = useState([]);
  const [adminRole, setAdminRole] = useState(null);
  
  const { user } = useContext(AuthContext);

  // Vérifier si l'utilisateur a une permission
  const hasPermission = (permission) => adminPermissions.includes(permission);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const res = await api.get('/admin/team/permissions');
        setAdminPermissions(res.data.permissions || []);
        setAdminRole(res.data.admin_role);
        loadData(res.data.permissions || []);
      } catch (err) {
        console.error(err);
        loadData([]);
      }
    };
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset pagination quand les filtres/recherches changent
  useEffect(() => { setUsersPage(1); }, [searchUsers, filterRole]);
  useEffect(() => { setProjectsPage(1); }, [searchProjects]);
  useEffect(() => { setServicesPage(1); }, [searchServices]);
  useEffect(() => { setRequestsPage(1); }, [searchRequests, filterRequestStatus]);

  const loadData = async (permissions = adminPermissions) => {
    setLoading(true);
    try {
      // Charger uniquement les données autorisées
      const requests = [];
      
      if (permissions.includes('users') || permissions.includes('overview')) {
        requests.push(api.get('/users'));
      } else {
        requests.push(Promise.resolve({ data: [] }));
      }
      
      if (permissions.includes('requests') || permissions.includes('overview')) {
        requests.push(api.get('/admin/author-requests'));
      } else {
        requests.push(Promise.resolve({ data: [] }));
      }
      
      if (permissions.includes('projects') || permissions.includes('overview')) {
        requests.push(api.get('/projects'));
      } else {
        requests.push(Promise.resolve({ data: [] }));
      }
      
      if (permissions.includes('services') || permissions.includes('overview')) {
        requests.push(api.get('/services'));
      } else {
        requests.push(Promise.resolve({ data: [] }));
      }
      
      if (permissions.includes('payments') || permissions.includes('overview')) {
        requests.push(api.get('/platform-payments/stats').catch(() => ({ data: {} })));
      } else {
        requests.push(Promise.resolve({ data: {} }));
      }
      
      if (permissions.includes('reports') || permissions.includes('overview')) {
        requests.push(api.get('/reports/stats').catch(() => ({ data: {} })));
      } else {
        requests.push(Promise.resolve({ data: {} }));
      }
      
      if (permissions.includes('settings')) {
        requests.push(api.get('/services/payment-instructions').catch(() => ({ data: {} })));
      } else {
        requests.push(Promise.resolve({ data: {} }));
      }
      
      if (permissions.includes('accounts') || permissions.includes('overview')) {
        requests.push(api.get('/account-management/deletion-requests').catch(() => ({ data: { requests: [] } })));
      } else {
        requests.push(Promise.resolve({ data: { requests: [] } }));
      }

      const [usersRes, requestsRes, projectsRes, servicesRes, paymentsStatsRes, reportsStatsRes, paymentGuideRes, deletionRequestsRes] = await Promise.all(requests);
      
      const usersData = usersRes.data || [];
      const requestsData = requestsRes.data || [];
      const projectsData = projectsRes.data || [];
      const servicesData = servicesRes.data || [];
      const paymentsStats = paymentsStatsRes.data || {};
      const reportsStats = reportsStatsRes.data || {};
      const deletionRequests = deletionRequestsRes.data?.requests || [];
      const pendingDeletionCount = deletionRequests.filter(r => r.statut === 'en_attente').length;
      
      setUsers(usersData);
      setAuthorRequests(requestsData);
      setProjects(projectsData);
      setServices(servicesData);
      
      // Charger le guide de paiement
      if (paymentGuideRes.data?.payment_instructions) {
        setPaymentGuide(paymentGuideRes.data.payment_instructions);
      }
      if (paymentGuideRes.data?.payment_qr_image) {
        setPaymentQrImage(paymentGuideRes.data.payment_qr_image);
      }
      
      setStats({
        users: usersData.length,
        authors: usersData.filter(u => u.role === 'author').length,
        admins: usersData.filter(u => u.role === 'admin').length,
        projects: projectsData.length,
        services: servicesData.length,
        pendingRequests: requestsData.filter(r => r.statut === 'en_attente').length,
        overduePayments: paymentsStats.projets_en_retard || 0,
        pendingReports: reportsStats.en_attente || 0,
        pendingDeletionRequests: pendingDeletionCount
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId, userId) => {
    openDecisionModal(authorRequests.find(r => r.id === requestId), 'accepte');
  };

  const handleRejectRequest = async (requestId) => {
    openDecisionModal(authorRequests.find(r => r.id === requestId), 'refuse');
  };

  const openDecisionModal = (request, type) => {
    setSelectedRequest(request);
    setDecisionType(type);
    setJustification('');
    setShowDecisionModal(true);
  };

  const submitDecision = async () => {
    if (!selectedRequest) return;
    try {
      await api.put(`/admin/author-requests/${selectedRequest.id}`, { 
        statut: decisionType,
        justification: justification 
      });
      setShowDecisionModal(false);
      loadData();
    } catch (err) {
      toast.error('Erreur lors du traitement de la demande');
    }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    if (!(await confirm('Changer le rôle ?'))) return;
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!(await confirm('Supprimer cet utilisateur ?', { type: 'danger' }))) return;
    try {
      await api.delete(`/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!(await confirm('Supprimer ce projet ?', { type: 'danger' }))) return;
    try {
      await api.delete(`/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Service management
  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        titre: service.titre || service.nom || '',
        description: service.description || '',
        categorie: service.categorie || service.type || 'autre',
        tarif: service.tarif || service.prix || ''
      });
      setLogoPreview(service.logo ? getLogoUrl(service.logo) : null);
    } else {
      setEditingService(null);
      setServiceForm({ titre: '', description: '', categorie: 'autre', tarif: '' });
      setLogoPreview(null);
    }
    setLogoFile(null);
    setShowServiceModal(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      // Créer FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('titre', serviceForm.titre);
      formData.append('description', serviceForm.description);
      formData.append('categorie', serviceForm.categorie);
      formData.append('tarif', serviceForm.tarif);
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      if (editingService) {
        await api.post(`/admin/services/${editingService.id}/update`, formData);
      } else {
        await api.post('/admin/services', formData);
      }
      loadData();
      setShowServiceModal(false);
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!(await confirm('Supprimer ce service partenaire ?', { type: 'danger' }))) return;
    try {
      await api.delete(`/services/${serviceId}`);
      setServices(services.filter(s => s.id !== serviceId));
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.nom?.toLowerCase().includes(searchUsers.toLowerCase()) ||
                         u.email?.toLowerCase().includes(searchUsers.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // Pagination des utilisateurs
  const usersTotalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice((usersPage - 1) * itemsPerPage, usersPage * itemsPerPage);

  // Filtrage et pagination des projets
  const filteredProjects = projects.filter(p => {
    if (!searchProjects) return true;
    const s = searchProjects.toLowerCase();
    return (p.titre || '').toLowerCase().includes(s) ||
           (p.author_nom || '').toLowerCase().includes(s) ||
           (p.description || '').toLowerCase().includes(s);
  });
  const projectsTotalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedProjects = filteredProjects.slice((projectsPage - 1) * itemsPerPage, projectsPage * itemsPerPage);

  // Filtrage et pagination des services
  const filteredServices = services.filter(s => {
    if (!searchServices) return true;
    const search = searchServices.toLowerCase();
    return (s.titre || s.nom || '').toLowerCase().includes(search) ||
           (s.description || '').toLowerCase().includes(search) ||
           (s.categorie || '').toLowerCase().includes(search);
  });
  const servicesTotalPages = Math.ceil(filteredServices.length / itemsPerPage);
  const paginatedServices = filteredServices.slice((servicesPage - 1) * itemsPerPage, servicesPage * itemsPerPage);

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700',
      author: 'bg-green-100 text-green-700',
      user: 'bg-blue-100 text-blue-700'
    };
    return styles[role] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      {/* Header sticky */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-2xl shadow-lg shadow-indigo-200">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Administration</h1>
                <p className="text-sm text-gray-500">Bienvenue, {user?.nom || 'Admin'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium text-sm flex items-center gap-1">
                <Home className="w-4 h-4" /> Accueil
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Grid - Nouveau design */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Users Card */}
          <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all cursor-pointer group" onClick={() => setActiveTab('users')}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-xs text-gray-400 uppercase tracking-wide">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-800">{stats.users}</div>
            <div className="text-sm text-gray-500 mt-1">Utilisateurs</div>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-xs text-gray-500">{stats.authors} auteurs</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-xs text-gray-500">{stats.admins} admins</span>
              </div>
            </div>
          </div>

          {/* Projects Card */}
          <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all cursor-pointer group" onClick={() => setActiveTab('projects')}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                <FolderOpen className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-xs text-gray-400 uppercase tracking-wide">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-800">{stats.projects}</div>
            <div className="text-sm text-gray-500 mt-1">Projets</div>
          </div>

          {/* Services Card */}
          <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all cursor-pointer group" onClick={() => setActiveTab('services')}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                <Briefcase className="w-6 h-6 text-emerald-600" />
              </div>
              <span className="text-xs text-gray-400 uppercase tracking-wide">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-800">{stats.services}</div>
            <div className="text-sm text-gray-500 mt-1">Services</div>
          </div>

          {/* Alerts Card */}
          <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl p-5 shadow-lg text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-2xl">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-white/70 uppercase tracking-wide">Attention</span>
            </div>
            <div className="text-3xl font-bold">{stats.pendingRequests + stats.pendingReports + stats.overduePayments + stats.pendingDeletionRequests}</div>
            <div className="text-sm text-white/80 mt-1">Actions requises</div>
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/20">
              {stats.pendingRequests > 0 && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">{stats.pendingRequests} demandes</span>
              )}
              {stats.pendingReports > 0 && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">{stats.pendingReports} signalements</span>
              )}
              {stats.overduePayments > 0 && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">{stats.overduePayments} retards</span>
              )}
              {stats.pendingDeletionRequests > 0 && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">{stats.pendingDeletionRequests} suppressions</span>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions - Nouveau design */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-lg"><Zap className="w-4 h-4 text-indigo-600" /></span>
            Actions rapides
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Services */}
            {(hasPermission('services') || hasPermission('service_requests')) && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 border border-purple-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center text-white text-lg shadow-lg shadow-purple-200">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-purple-800">Services</h4>
              </div>
              <div className="flex flex-col gap-2">
                {hasPermission('service_requests') && (
                <Link
                  to="/admin/service-requests"
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 text-sm"
                >
                  <ClipboardList className="w-4 h-4" /> Gestion des demandes
                </Link>
                )}
                {hasPermission('services_edit') && (
                <button
                  onClick={() => { setActiveTab('services'); openServiceModal(); }}
                  className="px-3 py-2.5 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition flex items-center gap-2 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" /> Nouveau service
                </button>
                )}
              </div>
            </div>
            )}

            {/* Abonnements */}
            {(hasPermission('settings') || hasPermission('subscriptions')) && (
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 border border-emerald-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white text-lg shadow-lg shadow-emerald-200">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-emerald-800">Abonnements</h4>
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  to="/admin/subscription-plans"
                  className="px-3 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition flex items-center gap-2 text-sm font-medium shadow-lg shadow-emerald-200"
                >
                  <Package className="w-4 h-4" /> Gestion des packs
                </Link>
              </div>
            </div>
            )}

            {/* Paiements */}
            {hasPermission('payments') && (
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-5 border border-orange-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center text-white text-lg shadow-lg shadow-orange-200">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-orange-800">Paiements</h4>
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  to="/admin/platform-payments"
                  className="px-3 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center justify-between text-sm font-medium shadow-lg shadow-orange-200"
                >
                  <span className="flex items-center gap-2"><DollarSign className="w-4 h-4" /> Plateforme (3%)</span>
                  {stats.overduePayments > 0 && (
                    <span className="px-2 py-0.5 bg-white text-orange-600 text-xs rounded-full font-bold animate-pulse">{stats.overduePayments}</span>
                  )}
                </Link>
                {hasPermission('wallets') && (
                <Link
                  to="/admin/wallets"
                  className="px-3 py-2.5 bg-orange-100 text-orange-700 rounded-xl hover:bg-orange-200 transition flex items-center gap-2 text-sm font-medium"
                >
                  <CreditCard className="w-4 h-4" /> Portefeuilles & Dépôts
                </Link>
                )}
              </div>
            </div>
            )}

            {/* Modération */}
            {(hasPermission('reports') || hasPermission('accounts')) && (
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-5 border border-red-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center text-white text-lg shadow-lg shadow-red-200">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-red-800">Modération</h4>
              </div>
              <div className="flex flex-col gap-2">
                {hasPermission('reports') && (
                <Link
                  to="/admin/reports"
                  className="px-3 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition flex items-center justify-between text-sm font-medium shadow-lg shadow-red-200"
                >
                  <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Signalements</span>
                  {stats.pendingReports > 0 && (
                    <span className="px-2 py-0.5 bg-white text-red-600 text-xs rounded-full font-bold animate-pulse">{stats.pendingReports}</span>
                  )}
                </Link>
                )}
                {hasPermission('accounts') && (
                <Link
                  to="/admin/account-management"
                  className="px-3 py-2.5 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition flex items-center justify-between text-sm font-medium"
                >
                  <span className="flex items-center gap-2"><Users className="w-4 h-4" /> Gestion des comptes</span>
                  {stats.pendingDeletionRequests > 0 && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold">{stats.pendingDeletionRequests}</span>
                  )}
                </Link>
                )}
              </div>
            </div>
            )}

            {/* Communication */}
            {hasPermission('announcements') && (
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-5 border border-indigo-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center text-white text-lg shadow-lg shadow-indigo-200">
                  <Megaphone className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-indigo-800">Communication</h4>
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  to="/admin/announcements"
                  className="px-3 py-2.5 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition flex items-center gap-2 text-sm font-medium shadow-lg shadow-indigo-200"
                >
                  <Megaphone className="w-4 h-4" /> Annonces
                </Link>
              </div>
            </div>
            )}

            {/* Projets */}
            {hasPermission('projects') && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-5 border border-green-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center text-white text-lg shadow-lg shadow-green-200">
                  <FolderOpen className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-green-800">Projets</h4>
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  to="/create-project"
                  className="px-3 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 transition flex items-center gap-2 text-sm font-medium shadow-lg shadow-green-200"
                >
                  <Plus className="w-4 h-4" /> Nouveau projet
                </Link>
              </div>
            </div>
            )}

            {/* Équipe Admin - Super Admin uniquement */}
            {hasPermission('team_management') && (
            <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-2xl p-5 border border-rose-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center text-white text-lg shadow-lg shadow-rose-200">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-rose-800">Équipe Admin</h4>
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  to="/admin/team"
                  className="px-3 py-2.5 bg-rose-500 text-white rounded-xl hover:bg-rose-600 transition flex items-center gap-2 text-sm font-medium shadow-lg shadow-rose-200"
                >
                  <Users className="w-4 h-4" /> Gérer l'équipe
                </Link>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Badge du rôle */}
        {adminRole && (
          <div className="mb-6 flex items-center gap-3">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
              adminRole === 'super_admin' ? 'bg-red-100 text-red-800' :
              adminRole === 'moderator' ? 'bg-purple-100 text-purple-800' :
              adminRole === 'support' ? 'bg-blue-100 text-blue-800' :
              adminRole === 'accountant' ? 'bg-green-100 text-green-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {adminRole === 'super_admin' && <><Crown className="w-4 h-4 inline" /> Super Admin</>}
              {adminRole === 'moderator' && <><Shield className="w-4 h-4 inline" /> Modérateur</>}
              {adminRole === 'support' && <><MessageCircle className="w-4 h-4 inline" /> Support</>}
              {adminRole === 'accountant' && <><DollarSign className="w-4 h-4 inline" /> Comptable</>}
            </span>
            <span className="text-sm text-gray-500">
              {adminPermissions.length} permissions actives
            </span>
          </div>
        )}

        {/* Tabs - Afficher uniquement les tabs autorisés */}
        <div className="bg-white rounded-2xl shadow-sm mb-6 overflow-hidden">
          <div className="flex overflow-x-auto">
            {[
              { id: 'overview', label: <><BarChart3 className="w-4 h-4 inline" /> Vue d'ensemble</>, perm: 'overview' },
              { id: 'users', label: <><Users className="w-4 h-4 inline" /> Utilisateurs</>, perm: 'users' },
              { id: 'requests', label: <><FileText className="w-4 h-4 inline" /> Demandes</>, perm: 'requests', badge: stats.pendingRequests },
              { id: 'projects', label: <><FolderOpen className="w-4 h-4 inline" /> Projets</>, perm: 'projects' },
              { id: 'services', label: <><Briefcase className="w-4 h-4 inline" /> Services</>, perm: 'services' },
              { id: 'settings', label: <><Settings className="w-4 h-4 inline" /> Paramètres</>, perm: 'settings' }
            ].filter(tab => hasPermission(tab.perm)).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 py-4 px-6 text-center font-medium transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-indigo-600 border-indigo-500 bg-indigo-50/50' 
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">{tab.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Users */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-lg"><Users className="w-4 h-4 text-blue-600" /></span>
                  Utilisateurs récents
                </h3>
                <button onClick={() => setActiveTab('users')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                  Voir tout →
                </button>
              </div>
              <div className="space-y-3">
                {[...users]
                  .sort((a, b) => new Date(b.date_inscription || 0) - new Date(a.date_inscription || 0))
                  .slice(0, 6)
                  .map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center font-bold text-white text-sm">
                        {u.nom?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{u.nom}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${getRoleBadge(u.role)}`}>{u.role}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Requests */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-lg"><Clock className="w-4 h-4 text-amber-600" /></span>
                  Demandes en attente
                </h3>
                <button onClick={() => setActiveTab('requests')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                  Voir tout →
                </button>
              </div>
              {authorRequests.filter(r => r.statut === 'en_attente').length > 0 ? (
                <div className="space-y-3">
                  {authorRequests.filter(r => r.statut === 'en_attente').slice(0, 5).map(r => (
                    <div key={r.id} className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-100">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {r.photo_profil ? (
                            <img
                              src={getProfilePhotoUrl(r.photo_profil)}
                              alt={r.user_nom || `User #${r.user_id}`}
                              className="w-8 h-8 rounded-lg object-cover border-2 border-amber-200"
                            />
                          ) : (
                            <span className="inline-flex w-8 h-8 bg-amber-200 rounded-lg text-amber-700 font-bold text-sm items-center justify-center">
                              {(r.user_nom?.[0] || 'U').toUpperCase()}
                            </span>
                          )}
                          <span className="font-semibold text-gray-800">{r.user_nom || `User #${r.user_id}`}</span>
                        </div>
                        <p className="text-sm text-amber-700">Demande le rôle auteur</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApproveRequest(r.id, r.user_id)}
                          className="w-9 h-9 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center justify-center shadow-sm"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRejectRequest(r.id)}
                          className="w-9 h-9 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center justify-center shadow-sm"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="text-5xl mb-4 block"><CheckCircle className="w-12 h-12 text-green-500 mx-auto" /></span>
                  <p className="text-gray-500">Aucune demande en attente</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Filters */}
            <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row gap-4 bg-gray-50/50">
              <div className="flex-1">
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><Search className="w-4 h-4" /></span>
                  <input
                    type="text"
                    placeholder='Rechercher des utilisateurs...'
                    value={searchUsers}
                    onChange={(e) => setSearchUsers(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                  />
                </div>
              </div>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white font-medium"
              >
                <option value="all">Tous les rôles</option>
                <option value="user">Utilisateur</option>
                <option value="author">Auteur</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Utilisateur</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Rôle</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedUsers.map(u => (
                    <tr key={u.id} className="hover:bg-indigo-50/30 transition">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center font-bold text-white text-sm shadow-sm">
                            {u.nom?.[0]?.toUpperCase()}
                          </div>
                          <span className="font-semibold text-gray-800">{u.nom}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">{u.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={u.role}
                          onChange={(e) => handleChangeUserRole(u.id, e.target.value)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${getRoleBadge(u.role)} border-0 cursor-pointer shadow-sm`}
                          disabled={u.id === user?.id}
                        >
                          <option value="user">Utilisateur</option>
                          <option value="author">Auteur</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.id !== user?.id && (
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="w-9 h-9 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition flex items-center justify-center"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Users */}
            {usersTotalPages > 1 && (
              <div className="flex justify-center items-center gap-3 p-6 border-t border-gray-100 bg-gray-50/50">
                <button
                  onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                  disabled={usersPage === 1}
                  className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 font-medium shadow-sm transition"
                >
                  ← Précédent
                </button>
                <span className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-xl border border-gray-200">
                  Page {usersPage} sur {usersTotalPages} ({filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''})
                </span>
                <button
                  onClick={() => setUsersPage(p => Math.min(usersTotalPages, p + 1))}
                  disabled={usersPage === usersTotalPages}
                  className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 font-medium shadow-sm transition"
                >
                  Suivant →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-6">
            {/* Barre de recherche, filtres et tri */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Recherche */}
                <div className="flex-1">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"><Search className="w-4 h-4" /></span>
                    <input
                      type="text"
                      placeholder="Rechercher par nom, email..."
                      value={searchRequests}
                      onChange={(e) => setSearchRequests(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50/50"
                    />
                  </div>
                </div>
                
                {/* Filtre par statut */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Statut:</span>
                  <select
                    value={filterRequestStatus}
                    onChange={(e) => setFilterRequestStatus(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 font-medium bg-white"
                  >
                    <option value="all">Tous</option>
                    <option value="en_attente">En attente</option>
                    <option value="valide">Approuvés</option>
                    <option value="refuse">Refusés</option>
                  </select>
                </div>
                
                {/* Tri */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Trier:</span>
                  <select
                    value={sortRequests}
                    onChange={(e) => setSortRequests(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 font-medium bg-white"
                  >
                    <option value="newest">Plus récent</option>
                    <option value="oldest">Plus ancien</option>
                    <option value="name_asc">Nom (A-Z)</option>
                    <option value="name_desc">Nom (Z-A)</option>
                  </select>
                </div>
              </div>
              
              {/* Compteurs rapides */}
              <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={() => setFilterRequestStatus('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${filterRequestStatus === 'all' ? 'bg-indigo-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Tous ({authorRequests.length})
                </button>
                <button
                  onClick={() => setFilterRequestStatus('en_attente')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${filterRequestStatus === 'en_attente' ? 'bg-amber-500 text-white shadow-md' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
                >
                  <Clock className="w-4 h-4 inline" /> En attente ({authorRequests.filter(r => r.statut === 'en_attente').length})
                </button>
                <button
                  onClick={() => setFilterRequestStatus('valide')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${filterRequestStatus === 'valide' ? 'bg-green-500 text-white shadow-md' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                >
                  <CheckCircle className="w-4 h-4 inline" /> Approuvés ({authorRequests.filter(r => r.statut === 'valide').length})
                </button>
                <button
                  onClick={() => setFilterRequestStatus('refuse')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${filterRequestStatus === 'refuse' ? 'bg-red-500 text-white shadow-md' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                >
                  <XCircle className="w-4 h-4 inline" /> Refusés ({authorRequests.filter(r => r.statut === 'refuse').length})
                </button>
              </div>
            </div>

            {/* Liste des demandes filtrées */}
            {(() => {
              // Filtrer les demandes
              let filteredRequests = authorRequests.filter(r => {
                const matchesSearch = searchRequests === '' || 
                  (r.user_nom && r.user_nom.toLowerCase().includes(searchRequests.toLowerCase())) ||
                  (r.user_email && r.user_email.toLowerCase().includes(searchRequests.toLowerCase())) ||
                  (r.nom_complet && r.nom_complet.toLowerCase().includes(searchRequests.toLowerCase())) ||
                  (r.telephone && r.telephone.includes(searchRequests));
                
                const matchesStatus = filterRequestStatus === 'all' || r.statut === filterRequestStatus;
                
                return matchesSearch && matchesStatus;
              });
              
              // Trier les demandes
              filteredRequests = [...filteredRequests].sort((a, b) => {
                switch (sortRequests) {
                  case 'newest':
                    return new Date(b.date_demande || 0) - new Date(a.date_demande || 0);
                  case 'oldest':
                    return new Date(a.date_demande || 0) - new Date(b.date_demande || 0);
                  case 'name_asc':
                    return (a.user_nom || '').localeCompare(b.user_nom || '');
                  case 'name_desc':
                    return (b.user_nom || '').localeCompare(a.user_nom || '');
                  default:
                    return 0;
                }
              });
              
              // Pagination
              const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
              const paginatedRequests = filteredRequests.slice((requestsPage - 1) * itemsPerPage, requestsPage * itemsPerPage);
              
              if (filteredRequests.length === 0) {
                return (
                  <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
                    <span className="text-7xl mb-6 block"><Search className="w-16 h-16 text-gray-300 mx-auto" /></span>
                    <h3 className="text-2xl font-bold text-gray-700 mb-3">Aucune demande trouvée</h3>
                    <p className="text-gray-500 text-lg">
                      {searchRequests || filterRequestStatus !== 'all' 
                        ? "Essayez de modifier vos critères de recherche"
                        : "Aucune demande d'auteur pour le moment"}
                    </p>
                  </div>
                );
              }
              
              return (
                <>
                  {paginatedRequests.map(r => (
                <div key={r.id} className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition">
                  <div className="flex flex-col gap-4">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          {r.photo_profil ? (
                            <img
                              src={getProfilePhotoUrl(r.photo_profil)}
                              alt={r.user_nom || `User #${r.user_id}`}
                              className="w-12 h-12 rounded-xl object-cover border-2 border-indigo-200 shadow-sm"
                            />
                          ) : (
                            <span className="inline-flex w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl text-white font-bold text-lg items-center justify-center shadow-sm">
                              {(r.user_nom?.[0] || 'U').toUpperCase()}
                            </span>
                          )}
                          <div>
                            <span className="text-lg font-bold text-gray-800 block">{r.user_nom || `User #${r.user_id}`}</span>
                            <p className="text-sm text-gray-500">{r.user_email}</p>
                          </div>
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                            r.statut === 'en_attente' ? 'bg-amber-100 text-amber-700' :
                            r.statut === 'valide' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {r.statut === 'valide' ? <><CheckCircle className="w-3 h-3 inline" /> Approuvé</> : r.statut === 'refuse' ? <><XCircle className="w-3 h-3 inline" /> Refusé</> : <><Clock className="w-3 h-3 inline" /> En attente</>}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 flex items-center gap-2 mt-2">
                          <span className="bg-gray-100 px-2 py-1 rounded-lg"><Calendar className="w-3 h-3 inline" /> {new Date(r.date_demande || Date.now()).toLocaleDateString('fr-FR')}</span>
                        </p>
                      </div>
                      {r.statut === 'en_attente' && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApproveRequest(r.id, r.user_id)}
                            className="px-5 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 transition font-semibold shadow-sm"
                          >
                            <Check className="w-4 h-4 inline" /> Approuver
                          </button>
                          <button
                            onClick={() => handleRejectRequest(r.id)}
                            className="px-5 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition font-semibold shadow-sm"
                          >
                            <X className="w-4 h-4 inline" /> Rejeter
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Informations personnelles */}
                    <div className="border-t border-gray-100 pt-4 mt-2">
                      <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-lg bg-indigo-100 flex items-center justify-center text-sm"><ClipboardList className="w-3 h-3 text-indigo-600" /></span>
                        Informations personnelles soumises
                      </h4>
                      
                      {(r.nom_complet || r.date_naissance || r.telephone || r.adresse || r.piece_identite) ? (
                        <>
                          <div className="grid md:grid-cols-2 gap-4 text-sm bg-gray-50 p-5 rounded-xl">
                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs uppercase tracking-wide">Nom complet</span>
                              <span className="font-medium text-gray-800 mt-1">{r.nom_complet || '-'}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs uppercase tracking-wide">Date de naissance</span>
                              <span className="font-medium text-gray-800 mt-1">
                                {r.date_naissance ? new Date(r.date_naissance).toLocaleDateString('fr-FR') : '-'}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs uppercase tracking-wide">Téléphone</span>
                              <span className="font-medium text-gray-800 mt-1">{r.telephone || '-'}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs uppercase tracking-wide">Adresse</span>
                              <span className="font-medium text-gray-800 mt-1">{r.adresse || '-'}</span>
                            </div>
                          </div>
                          
                          {/* Pièce d'identité avec prévisualisation */}
                          {r.piece_identite && (
                            <div className="mt-4 p-5 bg-blue-50 rounded-xl border border-blue-100">
                              <span className="text-gray-700 text-sm font-bold flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center text-sm"><CreditCard className="w-3 h-3 text-blue-600" /></span>
                                Pièce d'identité soumise
                              </span>
                              <div className="mt-3 flex items-center gap-4">
                                {r.piece_identite.match(/\.(jpg|jpeg|png)$/i) ? (
                                  <img 
                                    src={getIdentityDocUrl(r.piece_identite)}
                                    alt="Pièce d'identité"
                                    className="max-h-48 rounded-xl border-2 border-blue-200 shadow-md"
                                  />
                                ) : (
                                  <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200">
                                    <span className="text-4xl"><FileText className="w-8 h-8 text-gray-500" /></span>
                                    <span className="text-gray-600 font-medium">Document PDF</span>
                                  </div>
                                )}
                                <a 
                                  href={getIdentityDocUrl(r.piece_identite)} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium shadow-sm"
                                >
                                  Ouvrir en grand →
                                </a>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="bg-amber-50 text-amber-700 p-5 rounded-xl text-sm border border-amber-200 flex items-center gap-3">
                          <span className="text-2xl"><AlertTriangle className="w-5 h-5 text-amber-600 inline" /></span>
                          <span>Cette demande a été soumise sans les informations personnelles requises (ancienne version du formulaire).</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Justification admin (si décision prise) */}
                    {r.justification_admin && (
                      <div className="border-t border-gray-100 pt-4 mt-2">
                        <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center text-sm"><MessageCircle className="w-3 h-3 text-purple-600" /></span>
                          Justification de la décision
                        </h4>
                        <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-xl">{r.justification_admin}</p>
                      </div>
                    )}
                  </div>
                </div>
                ))}
                  
                {/* Pagination Requests */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-3 mt-6 bg-white rounded-2xl p-4 shadow-sm">
                    <button
                      onClick={() => setRequestsPage(p => Math.max(1, p - 1))}
                      disabled={requestsPage === 1}
                      className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 font-medium transition"
                    >
                      ← Précédent
                    </button>
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-4 py-2 rounded-xl">Page {requestsPage} sur {totalPages}</span>
                    <button
                      onClick={() => setRequestsPage(p => Math.min(totalPages, p + 1))}
                      disabled={requestsPage === totalPages}
                      className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 font-medium transition"
                    >
                      Suivant →
                    </button>
                  </div>
                )}
              </>
              );
            })()}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* Barre de recherche */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><Search className="w-4 h-4" /></span>
                <input
                  type="text"
                  placeholder="Rechercher un projet..."
                  value={searchProjects}
                  onChange={(e) => setSearchProjects(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50/50"
                />
              </div>
              {searchProjects && (
                <p className="text-sm text-gray-500 mt-3 font-medium">{filteredProjects.length} résultat(s)</p>
              )}
            </div>
            
            {paginatedProjects.length > 0 ? (
              <>
                {paginatedProjects.map(p => (
                  <div key={p.id} className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-lg shadow-sm"><Film className="w-5 h-5" /></span>
                          <h3 className="text-lg font-bold text-gray-800">{p.titre}</h3>
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2 ml-13 mb-2">{p.description}</p>
                        <p className="text-sm text-gray-400 ml-13 flex items-center gap-2">
                          <span className="bg-gray-100 px-2 py-1 rounded-lg"><User className="w-3 h-3 inline" /> {p.author_nom || `User #${p.author_id}`}</span>
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <a
                          href={`/project/${p.id}`}
                          className="w-10 h-10 bg-indigo-100 text-indigo-700 rounded-xl hover:bg-indigo-200 transition flex items-center justify-center"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteProject(p.id)}
                          className="w-10 h-10 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Pagination Projects */}
                {projectsTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-3 mt-6 bg-white rounded-2xl p-4 shadow-sm">
                    <button
                      onClick={() => setProjectsPage(p => Math.max(1, p - 1))}
                      disabled={projectsPage === 1}
                      className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 font-medium transition"
                    >
                      ← Précédent
                    </button>
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-4 py-2 rounded-xl">Page {projectsPage} sur {projectsTotalPages}</span>
                    <button
                      onClick={() => setProjectsPage(p => Math.min(projectsTotalPages, p + 1))}
                      disabled={projectsPage === projectsTotalPages}
                      className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 font-medium transition"
                    >
                      Suivant →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
                <span className="text-7xl mb-6 block"><Film className="w-16 h-16 text-gray-300 mx-auto" /></span>
                <h3 className="text-2xl font-bold text-gray-700 mb-3">
                  {searchProjects ? 'Aucun projet trouvé' : 'Aucun projet'}
                </h3>
                {searchProjects && (
                  <p className="text-gray-500 text-lg">Essayez avec d'autres termes de recherche</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Header with Create Button */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-lg shadow-sm"><Briefcase className="w-5 h-5" /></span>
                Services Partenaires
              </h2>
              <button
                onClick={() => openServiceModal()}
                className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition font-semibold flex items-center gap-2 shadow-md"
              >
                <Plus className="w-4 h-4" /> Créer un service
              </button>
            </div>
            
            {/* Barre de recherche */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><Search className="w-4 h-4" /></span>
                <input
                  type="text"
                  placeholder="Rechercher un service..."
                  value={searchServices}
                  onChange={(e) => setSearchServices(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50/50"
                />
              </div>
              {searchServices && (
                <p className="text-sm text-gray-500 mt-3 font-medium">{filteredServices.length} résultat(s)</p>
              )}
            </div>

            {paginatedServices.length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedServices.map(s => (
                    <div key={s.id} className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition border border-gray-100">
                      {/* Logo partenaire */}
                      {s.logo && (
                        <div className="mb-5 flex justify-center">
                          <img 
                            src={getLogoUrl(s.logo)} 
                            alt={s.titre || s.nom}
                            className="h-20 w-auto object-contain"
                          />
                        </div>
                      )}
                      <div className="flex justify-between items-start mb-4">
                      <span className={`px-3 py-1.5 text-xs rounded-lg font-semibold ${
                        s.categorie === 'administratif' ? 'bg-blue-100 text-blue-700' :
                        s.categorie === 'assistance' ? 'bg-green-100 text-green-700' :
                        s.categorie === 'recherche' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {s.categorie || s.type || 'autre'}
                      </span>
                      {(s.tarif || s.prix) && (
                        <span className="text-lg font-bold text-indigo-600">{s.tarif || s.prix} XOF</span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">{s.titre || s.nom}</h3>
                    <p className="text-gray-600 text-sm mb-5 line-clamp-2">{s.description}</p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => openServiceModal(s)}
                        className="flex-1 px-4 py-2.5 bg-indigo-100 text-indigo-700 rounded-xl hover:bg-indigo-200 transition font-medium"
                      >
                        <Pencil className="w-4 h-4" /> Modifier
                      </button>
                      <button
                        onClick={() => handleDeleteService(s.id)}
                        className="px-4 py-2.5 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  ))}
                </div>
                
                {/* Pagination Services */}
                {servicesTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-3 mt-6 bg-white rounded-2xl p-4 shadow-sm">
                    <button
                      onClick={() => setServicesPage(p => Math.max(1, p - 1))}
                      disabled={servicesPage === 1}
                      className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 font-medium transition"
                    >
                      ← Précédent
                    </button>
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-4 py-2 rounded-xl">Page {servicesPage} sur {servicesTotalPages}</span>
                    <button
                      onClick={() => setServicesPage(p => Math.min(servicesTotalPages, p + 1))}
                      disabled={servicesPage === servicesTotalPages}
                      className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 font-medium transition"
                    >
                      Suivant →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
                <span className="text-7xl mb-6 block"><Briefcase className="w-16 h-16 text-gray-300 mx-auto" /></span>
                <h3 className="text-2xl font-bold text-gray-700 mb-3">
                  {searchServices ? 'Aucun service trouvé' : 'Aucun service'}
                </h3>
                {searchServices ? (
                  <p className="text-gray-500 text-lg">Essayez avec d'autres termes de recherche</p>
                ) : (
                  <>
                    <p className="text-gray-500 mb-6 text-lg">Créez des services partenaires pour vos utilisateurs</p>
                    <button
                      onClick={() => openServiceModal()}
                      className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition font-semibold shadow-md"
                    >
                      <Plus className="w-4 h-4" /> Créer le premier service
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Guide de paiement */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white text-lg shadow-sm"><CreditCard className="w-5 h-5" /></span>
                  Guide de paiement
                </h3>
                {paymentGuideSaved && (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <Check className="w-4 h-4" /> Sauvegardé
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm mb-5">
                Ce texte sera affiché aux utilisateurs lors du processus de souscription pour les guider sur les modalités de paiement.
              </p>
              
              <textarea
                value={paymentGuide}
                onChange={(e) => {
                  setPaymentGuide(e.target.value);
                  setPaymentGuideSaved(false);
                }}
                rows={8}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none font-mono text-sm"
                placeholder="Exemple:
Comment effectuer votre paiement ?

1. Ouvrez votre application Mobile Money (Orange Money, Wave, etc.)
2. Sélectionnez 'Paiement marchand' ou 'Transfert'
3. Entrez le numéro: XX XX XX XX XX
4. Indiquez le montant exact de votre abonnement
5. Validez et conservez le reçu

Important: Mentionnez votre email dans le motif du paiement pour faciliter la validation."
              />
              
              <div className="flex justify-between items-center mt-6">
                <p className="text-xs text-gray-400 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-5 h-5 flex items-center justify-center text-xs"><Lightbulb className="w-3 h-3" /></span>
                  Vous pouvez utiliser des emojis et des retours à la ligne pour structurer le texte.
                </p>
                <div className="flex gap-3">
                  {paymentGuide && (
                    <button
                      onClick={async () => {
                        if (await confirm('Êtes-vous sûr de vouloir supprimer le guide de paiement ?')) {
                          setPaymentGuideLoading(true);
                          try {
                            const token = localStorage.getItem('token');
                            await fetch(`${process.env.REACT_APP_API_URL}/services/payment-instructions`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            setPaymentGuide('');
                            setPaymentGuideSaved(true);
                            setTimeout(() => setPaymentGuideSaved(false), 3000);
                          } catch (error) {
                            console.error('Erreur:', error);
                          }
                          setPaymentGuideLoading(false);
                        }
                      }}
                      disabled={paymentGuideLoading}
                      className="px-5 py-2.5 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition disabled:opacity-50 font-medium"
                    >
                      <Trash2 className="w-4 h-4 inline" /> Supprimer
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      setPaymentGuideLoading(true);
                      try {
                        const token = localStorage.getItem('token');
                        await fetch(`${process.env.REACT_APP_API_URL}/services/payment-instructions`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({ instructions: paymentGuide })
                        });
                        setPaymentGuideSaved(true);
                        setTimeout(() => setPaymentGuideSaved(false), 3000);
                      } catch (error) {
                        console.error('Erreur:', error);
                      }
                      setPaymentGuideLoading(false);
                    }}
                    disabled={paymentGuideLoading}
                    className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 flex items-center gap-2 font-semibold shadow-md"
                  >
                    {paymentGuideLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 inline" /> Sauvegarder
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Image QR de paiement */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white text-lg shadow-sm"><Smartphone className="w-5 h-5" /></span>
                  Image QR de paiement
                </h3>
              </div>
              <p className="text-gray-500 text-sm mb-5">
                Ajoutez une image de QR code ou un visuel pour guider les utilisateurs sur le paiement. Cette image sera affichée avec les instructions de paiement.
              </p>
              
              {/* Image actuelle */}
              {(paymentQrImage || paymentQrPreview) && (
                <div className="mb-5">
                  <div className="relative inline-block">
                    <img
                      src={paymentQrPreview || getPaymentQrImageUrl(paymentQrImage)}
                      alt="QR code de paiement"
                      className="max-w-xs max-h-64 rounded-xl border-2 border-gray-200 shadow-sm object-contain"
                    />
                    {paymentQrImage && !paymentQrPreview && (
                      <button
                        onClick={async () => {
                          if (await confirm('Supprimer l\'image QR de paiement ?', { type: 'danger' })) {
                            setPaymentQrLoading(true);
                            try {
                              const token = localStorage.getItem('token');
                              await fetch(`${process.env.REACT_APP_API_URL}/services/payment-qr-image`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              setPaymentQrImage('');
                              setPaymentQrFile(null);
                              setPaymentQrPreview(null);
                            } catch (error) {
                              console.error('Erreur:', error);
                            }
                            setPaymentQrLoading(false);
                          }
                        }}
                        disabled={paymentQrLoading}
                        className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-md"
                        title="Supprimer l'image"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Upload */}
              <div className="flex items-center gap-4">
                <label className="flex-1">
                  <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-all">
                    <div className="text-center">
                      <span className="text-3xl block mb-1"><Camera className="w-6 h-6 text-gray-400 mx-auto" /></span>
                      <span className="text-sm text-gray-500">
                        {paymentQrFile ? paymentQrFile.name : 'Cliquez pour choisir une image'}
                      </span>
                      <span className="text-xs text-gray-400 block mt-1">JPG, PNG, GIF, WEBP (max 5 Mo)</span>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPaymentQrFile(file);
                        const reader = new FileReader();
                        reader.onloadend = () => setPaymentQrPreview(reader.result);
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
              </div>
              
              {paymentQrFile && (
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => {
                      setPaymentQrFile(null);
                      setPaymentQrPreview(null);
                    }}
                    className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={async () => {
                      setPaymentQrLoading(true);
                      try {
                        const token = localStorage.getItem('token');
                        const formData = new FormData();
                        formData.append('qr_image', paymentQrFile);
                        const res = await fetch(`${process.env.REACT_APP_API_URL}/services/payment-qr-image`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` },
                          body: formData
                        });
                        const data = await res.json();
                        if (data.success) {
                          setPaymentQrImage(data.payment_qr_image);
                          setPaymentQrFile(null);
                          setPaymentQrPreview(null);
                        } else {
                          toast.error(data.error || 'Erreur lors de l\'upload');
                        }
                      } catch (error) {
                        console.error('Erreur:', error);
                        toast.error('Erreur lors de l\'upload');
                      }
                      setPaymentQrLoading(false);
                    }}
                    disabled={paymentQrLoading}
                    className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 flex items-center gap-2 font-semibold shadow-md"
                  >
                    {paymentQrLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Upload...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 inline" /> Uploader l'image
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {/* Prévisualisation */}
            {(paymentGuide || paymentQrImage) && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-lg"><Eye className="w-4 h-4 text-indigo-600" /></span>
                  Prévisualisation
                </h3>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
                  {paymentGuide && (
                    <pre className="whitespace-pre-wrap text-gray-700 text-sm font-sans leading-relaxed">
                      {paymentGuide}
                    </pre>
                  )}
                  {paymentQrImage && (
                    <div className={paymentGuide ? 'mt-4 pt-4 border-t border-indigo-200' : ''}>
                      <p className="text-gray-600 text-sm font-medium mb-2 flex items-center gap-1"><Smartphone className="w-4 h-4 inline" /> QR Code de paiement :</p>
                      <img
                        src={getPaymentQrImageUrl(paymentQrImage)}
                        alt="QR code de paiement"
                        className="max-w-[200px] max-h-[200px] rounded-lg border border-gray-200 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Service Modal */}
        {showServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-7">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                  <span className="text-2xl">{editingService ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}</span>
                  {editingService ? 'Modifier le service' : 'Nouveau service partenaire'}
                </h2>
                <button
                  onClick={() => setShowServiceModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleServiceSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Titre du service</label>
                  <input
                    type="text"
                    value={serviceForm.titre}
                    onChange={(e) => setServiceForm({ ...serviceForm, titre: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="Ex: Aide juridique pour artistes"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                  <textarea
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                    placeholder="Décrivez le service proposé par le partenaire..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Catégorie</label>
                    <select
                      value={serviceForm.categorie}
                      onChange={(e) => setServiceForm({ ...serviceForm, categorie: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 font-medium"
                    >
                      <option value="administratif">Administratif</option>
                      <option value="assistance">Assistance</option>
                      <option value="recherche">Recherche</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Tarif (XOF)</label>
                    <input
                      type="number"
                      value={serviceForm.tarif}
                      onChange={(e) => setServiceForm({ ...serviceForm, tarif: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Logo partenaire */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Logo du partenaire</label>
                  <div className="flex items-center gap-4">
                    {logoPreview && (
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="h-20 w-auto object-contain border-2 border-indigo-200 rounded-xl p-2 bg-gray-50"
                      />
                    )}
                    <label className="flex-1 cursor-pointer">
                      <div className="px-4 py-4 border-2 border-dashed border-indigo-300 rounded-xl text-center hover:border-indigo-500 hover:bg-indigo-50 transition">
                        <span className="text-gray-500 font-medium">
                          {logoFile ? logoFile.name : <><Camera className="w-4 h-4 inline" /> Cliquez pour ajouter un logo</>}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowServiceModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition font-semibold shadow-md"
                  >
                    {editingService ? 'Enregistrer' : 'Créer le service'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Decision Modal pour approbation/refus de demande auteur */}
        {showDecisionModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-7 my-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                  <span className="text-3xl">{decisionType === 'accepte' ? <CheckCircle className="w-7 h-7 text-green-600" /> : <XCircle className="w-7 h-7 text-red-600" />}</span>
                  {decisionType === 'accepte' ? 'Approuver la demande' : 'Rejeter la demande'}
                </h2>
                <button
                  onClick={() => setShowDecisionModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
                >
                  ×
                </button>
              </div>
              
              {/* Résumé de la demande */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 mb-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  {selectedRequest.photo_profil ? (
                    <img
                      src={getProfilePhotoUrl(selectedRequest.photo_profil)}
                      alt={selectedRequest.user_nom}
                      className="w-10 h-10 rounded-xl object-cover border-2 border-indigo-200"
                    />
                  ) : (
                    <span className="inline-flex w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl text-white font-bold items-center justify-center">
                      {(selectedRequest.user_nom?.[0] || 'U').toUpperCase()}
                    </span>
                  )}
                  <div>
                    <span className="font-bold text-gray-800 block">{selectedRequest.user_nom}</span>
                    <p className="text-sm text-gray-500">{selectedRequest.user_email}</p>
                  </div>
                </div>
                {selectedRequest.nom_complet && (
                  <p className="text-sm text-gray-500 mt-2 ml-13">Nom complet: {selectedRequest.nom_complet}</p>
                )}
              </div>
              
              {/* Templates pré-écrits */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center"><FileText className="w-3 h-3 text-blue-600" /></span>
                  Choisir un message pré-écrit
                </label>
                <div className="grid gap-3">
                  {decisionType === 'accepte' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setJustification("Dossier complet et conforme. Bienvenue dans la communauté des auteurs DJAMANN !")}
                        className="text-left p-4 border border-green-200 rounded-xl hover:bg-green-50 transition text-sm"
                      >
                        <span className="font-bold text-green-700 block"><Check className="w-3 h-3 inline" /> Dossier complet</span>
                        <p className="text-gray-600 mt-2">Dossier complet et conforme. Bienvenue dans la communauté des auteurs DJAMANN !</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setJustification("Votre profil et votre expérience correspondent parfaitement à nos critères. Nous sommes ravis de vous accueillir parmi nos auteurs.")}
                        className="text-left p-4 border border-green-200 rounded-xl hover:bg-green-50 transition text-sm"
                      >
                        <span className="font-bold text-green-700 block"><Check className="w-3 h-3 inline" /> Profil correspondant</span>
                        <p className="text-gray-600 mt-2">Votre profil et votre expérience correspondent parfaitement à nos critères...</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setJustification("")}
                        className="text-left p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition text-sm"
                      >
                        <span className="font-bold text-gray-700 block"><Pencil className="w-3 h-3 inline" /> Message personnalisé</span>
                        <p className="text-gray-500 mt-2">Écrire votre propre message...</p>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setJustification("La pièce d'identité fournie est illisible ou de mauvaise qualité. Veuillez soumettre une nouvelle demande avec un document plus clair.")}
                        className="text-left p-4 border border-red-200 rounded-xl hover:bg-red-50 transition text-sm"
                      >
                        <span className="font-bold text-red-700 block"><X className="w-3 h-3 inline" /> Pièce d'identité illisible</span>
                        <p className="text-gray-600 mt-2">La pièce d'identité fournie est illisible ou de mauvaise qualité...</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setJustification("Les informations personnelles fournies sont incomplètes ou incorrectes. Veuillez vérifier et soumettre une nouvelle demande.")}
                        className="text-left p-4 border border-red-200 rounded-xl hover:bg-red-50 transition text-sm"
                      >
                        <span className="font-bold text-red-700 block"><X className="w-3 h-3 inline" /> Informations incomplètes</span>
                        <p className="text-gray-600 mt-2">Les informations personnelles fournies sont incomplètes ou incorrectes...</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setJustification("La pièce d'identité ne correspond pas aux informations fournies dans le formulaire. Veuillez vérifier la cohérence de vos données.")}
                        className="text-left p-4 border border-red-200 rounded-xl hover:bg-red-50 transition text-sm"
                      >
                        <span className="font-bold text-red-700 block"><X className="w-3 h-3 inline" /> Incohérence des données</span>
                        <p className="text-gray-600 mt-2">La pièce d'identité ne correspond pas aux informations fournies...</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setJustification("Votre demande ne répond pas à nos critères d'éligibilité actuels. N'hésitez pas à nous contacter pour plus d'informations.")}
                        className="text-left p-4 border border-red-200 rounded-xl hover:bg-red-50 transition text-sm"
                      >
                        <span className="font-bold text-red-700 block"><X className="w-3 h-3 inline" /> Critères non remplis</span>
                        <p className="text-gray-600 mt-2">Votre demande ne répond pas à nos critères d'éligibilité actuels...</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setJustification("")}
                        className="text-left p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition text-sm"
                      >
                        <span className="font-bold text-gray-700 block"><Pencil className="w-3 h-3 inline" /> Message personnalisé</span>
                        <p className="text-gray-500 mt-2">Écrire votre propre message...</p>
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Zone de texte pour personnalisation */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center"><MessageCircle className="w-3 h-3 text-purple-600" /></span>
                  Message final {decisionType === 'refuse' ? '(recommandé)' : '(optionnel)'}
                </label>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                  placeholder="Sélectionnez un message ci-dessus ou écrivez votre propre message..."
                />
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs"><Lightbulb className="w-3 h-3" /></span>
                  Ce message sera visible par le demandeur dans sa notification.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowDecisionModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={submitDecision}
                  className={`flex-1 px-4 py-3 text-white rounded-xl transition font-semibold shadow-md ${
                    decisionType === 'accepte' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {decisionType === 'accepte' ? <><Check className="w-4 h-4 inline" /> Approuver</> : <><X className="w-4 h-4 inline" /> Rejeter</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
